import BaseWidget, { escapeHtml } from './baseWidget.js';
import dashboardService from '../../services/dashboardService.js';

const PRIORITY_ORDER = ['low', 'medium', 'high'];
const PRIORITY_LABEL = {
    low: 'Normal',
    medium: 'Wichtig',
    high: 'Dringend',
};

const prioRank = (p) => {
    const idx = PRIORITY_ORDER.indexOf(p);
    return idx === -1 ? PRIORITY_ORDER.length : PRIORITY_ORDER.length - idx;
};

const compareTodos = (a, b) =>
    Number(a.erledigt) - Number(b.erledigt) || prioRank(a.prioritaet) - prioRank(b.prioritaet);

// Kurzer Scale-Impuls als Klick-Feedback: Da der Hover bereits die nächste Farbe
// zeigt und der Klick genau diese setzt, ändert sich die Farbe beim Klicken nicht –
// der Pop bestätigt die Aktion auch bei mehreren Klicks hintereinander.
const popPrioBtn = (btn) =>
    btn.animate(
        [{ transform: 'scale(1.2)' }, { transform: 'scale(0.9)' }, { transform: 'scale(1.2)' }],
        { duration: 200, easing: 'ease-out' },
    );

/**
 * Persönliche Todoliste des Benutzers, ausschließlich im localStorage gespeichert.
 */
export default class OwnTodoWidget extends BaseWidget {
    loadTodos() {
        return dashboardService.getTodos();
    }

    saveTodos(todos) {
        dashboardService.setTodos(todos);
    }

    async onRemove() {
        // Beim Entfernen des Widgets wird auch die Aufgabenliste verworfen,
        // damit die Warnung beim Löschen mit dem tatsächlichen Verhalten übereinstimmt.
        dashboardService.setTodos([]);
    }

    getRemovalWarning() {
        const todos = dashboardService.getTodos();
        const offen = todos.filter((t) => !t.erledigt).length;
        if (todos.length === 0) return null;
        if (offen > 0) {
            return `Möchten Sie das Aufgaben-Widget wirklich entfernen?\nDie Liste enthält noch ${offen} offene Aufgabe${offen === 1 ? '' : 'n'} und wird dabei vollständig gelöscht.`;
        }
        return 'Möchten Sie das Aufgaben-Widget wirklich entfernen?\nAlle eingetragenen Aufgaben werden dabei gelöscht.';
    }

    async onRender() {
        clearTimeout(this._prioResortTimer);
        const todos = this.loadTodos();
        if (!PRIORITY_ORDER.includes(this.draftPrioritaet)) this.draftPrioritaet = 'medium';
        this.bodyEl.innerHTML = `
            <div class="widget-add-row widget-add-row-prio">
                <textarea class="todo-input" rows="2" placeholder="Neue Aufgabe..."></textarea>
                <button type="button" class="widget-todo-prio todo-add-prio prio-${this.draftPrioritaet}" title="Priorität: ${PRIORITY_LABEL[this.draftPrioritaet]} (klicken zum Wechseln)" aria-label="Priorität ${PRIORITY_LABEL[this.draftPrioritaet]}"></button>
                <button type="button" class="todo-add" title="Hinzufügen"><i class="bi bi-plus-lg"></i></button>
            </div>
            <div class="widget-todo-legend" title="Klicken Sie den farbigen Punkt einer Aufgabe, um die Priorität zu ändern.">
                <span><span class="widget-todo-prio prio-low"></span>Normal</span>
                <span><span class="widget-todo-prio prio-medium"></span>Wichtig</span>
                <span><span class="widget-todo-prio prio-high"></span>Dringend</span>
            </div>
            <div class="widget-todo-list"></div>
        `;
        const list = this.bodyEl.querySelector('.widget-todo-list');
        if (todos.length === 0) {
            list.innerHTML = '<div class="widget-empty">Keine Aufgaben.</div>';
        } else {
            // Erledigte ans Ende, sonst nach Priorität (high zuerst)
            todos
                .map((t, i) => ({ t, i }))
                .sort((a, b) => compareTodos(a.t, b.t))
                .forEach(({ t, i }) => list.appendChild(this.renderItem(t, i, todos)));
        }
        const input = this.bodyEl.querySelector('.todo-input');
        const prioBtn = this.bodyEl.querySelector('.todo-add-prio');
        const addFn = () => {
            const value = input.value.trim();
            if (!value) return;
            todos.push({ text: value, erledigt: false, prioritaet: this.draftPrioritaet, datum: new Date().toISOString() });
            this.saveTodos(todos);
            this.refresh();
        };
        prioBtn.addEventListener('click', () => {
            const current = PRIORITY_ORDER.indexOf(this.draftPrioritaet);
            this.draftPrioritaet = PRIORITY_ORDER[(current + 1) % PRIORITY_ORDER.length];
            PRIORITY_ORDER.forEach((p) => prioBtn.classList.remove(`prio-${p}`));
            prioBtn.classList.add(`prio-${this.draftPrioritaet}`);
            const label = PRIORITY_LABEL[this.draftPrioritaet];
            prioBtn.title = `Priorität: ${label} (klicken zum Wechseln)`;
            prioBtn.setAttribute('aria-label', `Priorität ${label}`);
            // Vorschau des nächsten Prio-Farbtons unterdrücken, bis der Cursor den
            // Button verlässt – sonst blitzt direkt nach dem Klick die übernächste Farbe auf.
            prioBtn.classList.add('is-cycling');
            popPrioBtn(prioBtn);
            input.focus();
        });
        prioBtn.addEventListener('mouseleave', () => prioBtn.classList.remove('is-cycling'));
        this.bodyEl.querySelector('.todo-add').addEventListener('click', addFn);
        // Enter sendet ab; Shift+Enter erlaubt Mehrzeiligkeit (Slack/GitHub-Pattern).
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                addFn();
            }
        });
    }

    animateResort() {
        const list = this.bodyEl.querySelector('.widget-todo-list');
        if (!list) return;
        const items = Array.from(list.children).filter((el) => el.dataset && el.dataset.todoIndex !== undefined);
        if (items.length < 2) return;
        const todos = this.loadTodos();
        const oldTops = new Map(items.map((el) => [el, el.getBoundingClientRect().top]));
        const sorted = items.slice().sort((a, b) => {
            const ta = todos[Number(a.dataset.todoIndex)];
            const tb = todos[Number(b.dataset.todoIndex)];
            if (!ta || !tb) return 0;
            return compareTodos(ta, tb);
        });
        let changed = false;
        sorted.forEach((el, i) => {
            if (list.children[i] !== el) {
                list.insertBefore(el, list.children[i]);
                changed = true;
            }
        });
        if (!changed) return;
        sorted.forEach((el) => {
            const oldTop = oldTops.get(el);
            const newTop = el.getBoundingClientRect().top;
            const dy = oldTop - newTop;
            if (dy) {
                el.animate(
                    [{ transform: `translateY(${dy}px)` }, { transform: 'translateY(0)' }],
                    { duration: 280, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' },
                );
            }
        });
    }

    renderItem(todo, index, todos) {
        const item = document.createElement('div');
        const prio = PRIORITY_ORDER.includes(todo.prioritaet) ? todo.prioritaet : 'medium';
        item.className = 'widget-todo-item' + (todo.erledigt ? ' erledigt' : '');
        item.dataset.todoIndex = String(index);
        item.innerHTML = `
            <input type="checkbox" ${todo.erledigt ? 'checked' : ''} />
            <div class="widget-todo-text">${escapeHtml(todo.text)}</div>
            <button type="button" class="widget-todo-prio prio-${prio}" title="Priorität: ${PRIORITY_LABEL[prio]} (klicken zum Wechseln)" aria-label="Priorität ${PRIORITY_LABEL[prio]}"></button>
            <i class="bi bi-trash" title="Löschen"></i>
        `;
        item.querySelector('input').addEventListener('change', (e) => {
            todos[index].erledigt = e.target.checked;
            this.saveTodos(todos);
            this.refresh();
        });
        const prioBtn = item.querySelector('.widget-todo-prio');
        prioBtn.addEventListener('click', () => {
            const current = PRIORITY_ORDER.indexOf(todos[index].prioritaet);
            const next = PRIORITY_ORDER[(current + 1) % PRIORITY_ORDER.length];
            todos[index].prioritaet = next;
            this.saveTodos(todos);
            PRIORITY_ORDER.forEach((p) => prioBtn.classList.remove(`prio-${p}`));
            prioBtn.classList.add(`prio-${next}`);
            const label = PRIORITY_LABEL[next];
            prioBtn.title = `Priorität: ${label} (klicken zum Wechseln)`;
            prioBtn.setAttribute('aria-label', `Priorität ${label}`);
            // Vorschau des nächsten Prio-Farbtons unterdrücken, bis der Cursor den
            // Button verlässt – sonst blitzt direkt nach dem Klick die übernächste Farbe auf.
            prioBtn.classList.add('is-cycling');
            popPrioBtn(prioBtn);
            clearTimeout(this._prioResortTimer);
            this._prioResortTimer = setTimeout(() => this.animateResort(), 600);
        });
        prioBtn.addEventListener('mouseleave', () => prioBtn.classList.remove('is-cycling'));
        item.querySelector('.bi-trash').addEventListener('click', () => {
            todos.splice(index, 1);
            this.saveTodos(todos);
            this.refresh();
        });
        return item;
    }
}
